import { useState, forwardRef } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useDomainConfig } from "@/contexts/DomainConfigContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { prefetchRoute } from "@/routes/prefetch";

const Header = forwardRef<HTMLElement>((_, ref) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { config } = useDomainConfig();
  const { t } = useLanguage();

  const logoUrl = config?.logo ? `https://globalparkingtech.co.uk/logos/${config.logo}` : "";
  const phoneNumber = config?.customer_service || "";

  const navLinks = [
    { name: t("nav.home"), href: "/", isRoute: true },
    { name: t("nav.about"), href: "/about", isRoute: true },
    { name: "Services", href: "/services", isRoute: true },
    { name: t("nav.faqs"), href: "/faqs", isRoute: true },
    { name: "Blog", href: "/blogs", isRoute: true },
    { name: "Terms & Conditions", href: "/terms", isRoute: true },
    { name: "Privacy Policy", href: "/privacy", isRoute: true },
    { name: t("nav.contact"), href: "/contact", isRoute: true },
  ];

  return (
    <header ref={ref} className="fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-lg border-b border-primary shadow-medium">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center min-h-[2.5rem] md:min-h-[3rem]">
            {logoUrl && (
              <img
                src={logoUrl}
                alt={config?.web_name || "Southampton Port Parking"}
                className="h-10 md:h-12 w-auto object-contain"
                width={230}
                height={48}
                fetchPriority="high"
              />
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = link.isRoute && location.pathname === link.href;
              return (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`text-sm font-medium transition-all duration-200 px-3 py-2 rounded-lg ${
                    isActive
                      ? "text-primary bg-accent"
                      : "text-primary-foreground/85 hover:text-accent hover:bg-white/10"
                  }`}
                  onMouseEnter={() => prefetchRoute(link.href)}
                  onFocus={() => prefetchRoute(link.href)}
                  onTouchStart={() => prefetchRoute(link.href)}
                >
                  {link.name}
                </Link>
              );
            })}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-3">
            {/* Phone CTA */}
            {phoneNumber && (
              <a
                href={`tel:${phoneNumber.replace(/\s/g, "")}`}
                className="hidden md:flex items-center gap-2 bg-accent text-accent-foreground px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-accent/90 transition-all shadow-soft hover:shadow-medium"
                dir="ltr"
                style={{ unicodeBidi: "isolate" }}
              >
                <Phone className="w-4 h-4 text-accent-foreground" />
                <span dir="ltr" style={{ unicodeBidi: "isolate" }}>{phoneNumber}</span>
              </a>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-primary-foreground hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6 text-primary-foreground" /> : <Menu className="w-6 h-6 text-primary-foreground" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-white/15 animate-fade-in bg-primary/95 backdrop-blur-lg">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => {
                const isActive = link.isRoute && location.pathname === link.href;
                return (
                  <Link
                    key={link.name}
                    to={link.href}
                    className={`text-sm font-medium transition-colors py-3 px-4 rounded-lg ${
                      isActive ? "text-primary bg-accent" : "text-primary-foreground/85 hover:text-accent hover:bg-white/10"
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                    onMouseEnter={() => prefetchRoute(link.href)}
                    onFocus={() => prefetchRoute(link.href)}
                    onTouchStart={() => prefetchRoute(link.href)}
                  >
                    {link.name}
                  </Link>
                );
              })}
              {phoneNumber && (
                <a
                  href={`tel:${phoneNumber.replace(/\s/g, "")}`}
                  className="flex items-center gap-2 text-accent font-semibold py-3 px-4"
                  dir="ltr"
                  style={{ unicodeBidi: "isolate" }}
                >
                  <Phone className="w-4 h-4 text-accent" />
                  <span dir="ltr" style={{ unicodeBidi: "isolate" }}>{phoneNumber}</span>
                </a>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
});

Header.displayName = "Header";

export default Header;
